const BLOCKED_NAME_VALUES = new Set([
  'aaa',
  'aaaa',
  'asdf',
  'qwerty',
  'test',
  'xxxxx',
  'admin',
  'abc',
  'xyz',
]);

const KEYBOARD_ROWS = ['qwertyuiop', 'asdfghjkl', 'zxcvbnm'];

const BLOCKED_EMAIL_LOCAL_PARTS = new Set(['aaa', 'abc', 'test', 'qwerty', 'asdf', 'admin']);
const BLOCKED_EMAIL_DOMAINS = new Set(['aaa', 'test', 'abc']);

const ALLOWED_TLDS = new Set(['com', 'org', 'net', 'in', 'co', 'io', 'edu']);

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const VOWELS = new Set(['a', 'e', 'i', 'o', 'u', 'y']);

function isRepeatedCharacter(value: string): boolean {
  return value.length > 1 && /^(.)\1+$/.test(value);
}

function containsKeyboardPattern(value: string): boolean {
  const lower = value.toLowerCase();
  return KEYBOARD_ROWS.some((row) => {
    for (let i = 0; i <= row.length - 4; i += 1) {
      const slice = row.slice(i, i + 4);
      if (lower.includes(slice) || lower.includes([...slice].reverse().join(''))) {
        return true;
      }
    }
    return false;
  });
}

function maxConsecutiveConsonants(word: string): number {
  let max = 0;
  let current = 0;

  for (const char of word.toLowerCase()) {
    if (/[a-z]/.test(char) && !VOWELS.has(char)) {
      current += 1;
      max = Math.max(max, current);
    } else {
      current = 0;
    }
  }

  return max;
}

function vowelCount(word: string): number {
  return [...word.toLowerCase()].filter((char) => VOWELS.has(char)).length;
}

function isValidNameWord(word: string): boolean {
  const normalized = word.toLowerCase();

  if (normalized.length < 2) {
    return false;
  }

  if (!/^[a-zA-Z]+$/.test(word)) {
    return false;
  }

  if (
    BLOCKED_NAME_VALUES.has(normalized) ||
    isRepeatedCharacter(normalized) ||
    containsKeyboardPattern(normalized)
  ) {
    return false;
  }

  const vowels = vowelCount(normalized);
  if (vowels === 0) {
    return false;
  }

  if (normalized.length >= 5 && vowels < 2 && vowels / normalized.length < 0.25) {
    return false;
  }

  if (maxConsecutiveConsonants(normalized) > 3) {
    return false;
  }

  return true;
}

function validateNamePart(value: string, label: string): string | null {
  const trimmed = value.trim();

  if (!trimmed) {
    return `${label} is required.`;
  }

  if (trimmed.length < 2) {
    return `Please enter a valid ${label.toLowerCase()}.`;
  }

  if (!/^[a-zA-Z\s'-]+$/.test(trimmed)) {
    return `Please enter a valid ${label.toLowerCase()}.`;
  }

  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.some((word) => !isValidNameWord(word))) {
    return `Please enter a valid ${label.toLowerCase()}.`;
  }

  return null;
}

export function validateFirstName(value: string): string | null {
  return validateNamePart(value, 'First name');
}

export function validateLastName(value: string): string | null {
  return validateNamePart(value, 'Last name');
}

export function validateEmailAddress(value: string): string | null {
  const trimmed = value.trim();

  if (!trimmed) {
    return 'Email address is required.';
  }

  if (!EMAIL_REGEX.test(trimmed)) {
    return 'Please enter a valid email address.';
  }

  const [localPart = '', domainPart = ''] = trimmed.split('@');
  const domainSegments = domainPart.toLowerCase().split('.');
  const domainName = domainSegments[0] ?? '';
  const tld = domainSegments[domainSegments.length - 1] ?? '';
  const normalizedLocal = localPart.toLowerCase();

  if (localPart.length < 2) {
    return 'Please enter a valid email address.';
  }

  if (!ALLOWED_TLDS.has(tld)) {
    return 'Please enter a valid email address.';
  }

  if (domainSegments.length < 2 || !domainName) {
    return 'Please enter a valid email address.';
  }

  if (
    BLOCKED_EMAIL_LOCAL_PARTS.has(normalizedLocal) ||
    BLOCKED_EMAIL_DOMAINS.has(domainName) ||
    isRepeatedCharacter(normalizedLocal) ||
    isRepeatedCharacter(domainName) ||
    containsKeyboardPattern(normalizedLocal)
  ) {
    return 'Please enter a valid email address.';
  }

  return null;
}

export function validateCompanyName(value: string): string | null {
  const trimmed = value.trim();

  if (!trimmed) {
    return null;
  }

  if (trimmed.length < 2) {
    return 'Please enter a valid company name.';
  }

  return null;
}

export function validateTitle(value: string): string | null {
  const trimmed = value.trim();

  if (!trimmed) {
    return null;
  }

  if (trimmed.length < 2) {
    return 'Please enter a valid title.';
  }

  return null;
}

export function validatePhoneNumber(value: string): string | null {
  const trimmed = value.trim();

  if (!trimmed) {
    return null;
  }

  if (!/^[\d()\s+-]+$/.test(trimmed)) {
    return 'Please enter a valid phone number.';
  }

  const digits = trimmed.replace(/\D/g, '');
  if (digits.length < 10 || digits.length > 15) {
    return 'Please enter a valid phone number.';
  }

  return null;
}

export function isBrochureFormValid(
  firstName: string,
  lastName: string,
  email: string,
): boolean {
  return (
    validateFirstName(firstName) === null &&
    validateLastName(lastName) === null &&
    validateEmailAddress(email) === null
  );
}
